import React from "react";
import * as Mui from "@mui/material";
import { InfoOutlined } from "@mui/icons-material";
import {
  designColors,
  designRadii,
  designShell,
  StatCard,
} from "services/constants/designTokens";
import { tooltipStyleProps } from "services/constants/Styles";

export { StatCard };

export const sectionCardSx = {
  ...designShell.sectionCard,
  p: 1.75,
};

// fontWeight 500 — lighter than a heading-weight 600/700 since this sits at
// the top of every card on Home and a heavier weight read as too bold/loud
// repeated across 6+ cards on one screen.
export const sectionTitleSx = {
  fontSize: 15,
  fontWeight: 500,
  color: designColors.slate900,
};

export const ctaButtonSx = designShell.compactOutlineButton;

// endAdornment sits between the title and the actionLabel button — e.g.
// UpcomingLeaveSection's calendar/list view toggle — so a section can add
// its own header control without every other caller needing to know about it
// (defaults to nothing, so existing SectionHeader usages are unaffected).
//
// `tooltip` is likewise optional: existing callers that don't pass one keep
// the bare (unwrapped) info icon exactly as before, so this doesn't change
// any card's look until it's given real message text. When present, it's
// wrapped in the same dark-styled Tooltip (tooltipStyleProps,
// services/constants/Styles) used for info icons elsewhere in the app.
export const SectionHeader = ({
  title,
  tooltip,
  actionLabel,
  onAction,
  endAdornment,
}) => (
  <Mui.Stack
    direction="row"
    alignItems="center"
    justifyContent="space-between"
    spacing={1}
  >
    <Mui.Stack direction="row" alignItems="center" spacing={0.85}>
      <Mui.Typography sx={sectionTitleSx}>{title}</Mui.Typography>
      {tooltip ? (
        <Mui.Tooltip title={tooltip} arrow slotProps={tooltipStyleProps}>
          <InfoOutlined
            sx={{ fontSize: 17, color: designColors.slate400, cursor: "pointer" }}
          />
        </Mui.Tooltip>
      ) : (
        <InfoOutlined sx={{ fontSize: 17, color: designColors.slate400 }} />
      )}
    </Mui.Stack>
    <Mui.Stack direction="row" alignItems="center" spacing={1} sx={{ flexShrink: 0 }}>
      {endAdornment}
      {actionLabel ? (
        <Mui.Button
          onClick={onAction}
          sx={{
            minWidth: 0,
            p: 0,
            textTransform: "none",
            fontSize: 13.5,
            fontWeight: 600,
            whiteSpace: "nowrap",
          }}
        >
          {actionLabel}
        </Mui.Button>
      ) : null}
    </Mui.Stack>
  </Mui.Stack>
);

export const ShiftCoverageCard = ({
  title,
  hours,
  dotColor,
  rows,
  progress,
  note = "",
}) => (
  <Mui.Box
    sx={{
      border: `1px solid ${designColors.slate200}`,
      borderRadius: designRadii.card,
      p: 1.4,
      backgroundColor: designColors.white,
      height: "100%",
      display: "flex",
      flexDirection: "column",
    }}
  >
    <Mui.Stack
      direction="row"
      alignItems="center"
      spacing={0.75}
      sx={{ mb: 1 }}
    >
      <Mui.Box
        sx={{
          width: 10,
          height: 10,
          borderRadius: "50%",
          backgroundColor: dotColor,
        }}
      />
      <Mui.Typography
        sx={{ fontSize: 14, fontWeight: 600, color: designColors.slate900 }}
      >
        {title}
      </Mui.Typography>
      <Mui.Typography sx={{ fontSize: 13, color: designColors.slate600 }}>
        {hours}
      </Mui.Typography>
    </Mui.Stack>
    <Mui.Stack spacing={0.9} sx={{ flex: 1 }}>
      {rows.map((row) => (
        <Mui.Stack
          key={row.label}
          direction="row"
          justifyContent="space-between"
          spacing={1}
        >
          <Mui.Typography sx={{ fontSize: 13.5, color: designColors.slate700 }}>
            {row.label}
          </Mui.Typography>
          <Mui.Typography
            sx={{
              fontSize: 13.5,
              fontWeight: 600,
              color: row.alert ? "#DC2626" : dotColor,
            }}
          >
            {row.value}
          </Mui.Typography>
        </Mui.Stack>
      ))}
      {note ? (
        <Mui.Typography
          sx={{ fontSize: 12.5, fontWeight: 600, color: "#7C3AED", pt: 0.25 }}
        >
          {note}
        </Mui.Typography>
      ) : null}
      <Mui.Box sx={{ pt: 0.5, mt: "auto" }}>
        <Mui.Stack
          direction="row"
          justifyContent="space-between"
          sx={{ mb: 0.5 }}
        >
          <Mui.Typography
            sx={{ fontSize: 13, fontWeight: 600, color: designColors.slate700 }}
          >
            Progress
          </Mui.Typography>
          <Mui.Typography
            sx={{ fontSize: 13, fontWeight: 600, color: dotColor }}
          >
            {progress}%
          </Mui.Typography>
        </Mui.Stack>
        <Mui.LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            height: 12,
            borderRadius: 999,
            backgroundColor: designColors.slate100,
            "& .MuiLinearProgress-bar": {
              borderRadius: 999,
              background: `linear-gradient(90deg, ${dotColor} 0%, ${dotColor}CC 100%)`,
            },
          }}
        />
      </Mui.Box>
    </Mui.Stack>
  </Mui.Box>
);
